'use client';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { IUIProfile } from '@/src/types/IUIProfile';
import { useCopyToClipboard } from '@/src/hooks/use-copy-to-clipboard';
import { cn } from '@workspace/ui/lib/utils';
import { useSetOnboardedMutation } from '@/src/hooks/use-profile';
import { redirect } from 'next/navigation';

const GoalsList = ({
  goals,
  onSkip,
  onShare,
  isCopied,
}: {
  goals: IOnboardingGoal[];
  onSkip: (g: IOnboardingGoal) => void;
  onShare: (g: IOnboardingGoal) => void;
  isCopied?: boolean;
}) => (
  <div className="md:p-3 p-2 max-h-fit md:max-h-[60vh] overflow-auto flex flex-1 flex-col gap-3 w-full md:w-[280px]">
    {goals.map((goal) => (
      <GoalItem
        key={goal.id}
        onSkip={onSkip}
        onShare={onShare}
        isCopied={isCopied}
        goal={goal}
      />
    ))}
  </div>
);

export interface IOnboardingGoal {
  id: number;
  title: string;
  completed: boolean;
  isShareAction?: boolean;
  link?: string;
}

let memoryStorage: { [key: string]: any } = {};
const mockStorage = {
  setItem: (key: string, value: any) => {
    memoryStorage[key] = value;
  },
  getItem: (key: string) => memoryStorage[key] ?? null,
  removeItem: (key: string) => {
    delete memoryStorage[key];
  },
};

const STORAGE_KEYS = {
  isShared: 'onboarding:isShared',
  isEarnSkipped: 'onboarding:isEarnSkipped',
  isMembershipSkipped: 'onboarding:isMembershipSkipped',
  completedGoals: 'onboarding:completedGoals',
};

const storage: Storage | typeof mockStorage =
  typeof window !== 'undefined' && window.localStorage
    ? window.localStorage
    : mockStorage;

const readBool = (key: string, def = false) => {
  try {
    const v = storage.getItem(key);
    return v === 'true' || v === true;
  } catch {
    return def;
  }
};

const readJSON = <T,>(key: string, def: T): T => {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : def;
  } catch {
    return def;
  }
};

const GoalItem = ({
  goal,
  onShare,
  onSkip,
  isCopied,
}: {
  goal: IOnboardingGoal;
  onShare: (goal: IOnboardingGoal) => void;
  onSkip: (goal: IOnboardingGoal) => void;
  isCopied?: boolean;
}) => {
  const handleClick = () => {
    if (goal.link) {
      redirect(goal.link);
    }
  };
  const canSkip = (goal.id === 1 || goal.id === 4) && !goal.completed;

  return (
    <div className="flex items-center gap-2 rounded-lg transition-colors">
      <div className="flex items-center justify-center size-4 rounded-full">
        {goal.completed ? (
          <Icon name="checked" className="size-4 text-white" />
        ) : (
          <div className="size-3.5 border rounded-full border-blue-primary bg-white" />
        )}
      </div>

      <div className="flex items-center flex-1 text-sm justify-between hover:bg-bg-gray px-1 rounded cursor-pointer hover:text-black">
        <div
          className={cn('text-black', {
            'text-black/60': goal.completed,
          })}
          onClick={handleClick}
        >
          {goal.title}
          {goal.isShareAction
            ? goal.completed &&
              isCopied && (
                <span className="absolute bottom-1 bg-gray-100 text-gray-text font-light p-2 ml-3 w-fit rounded-[8px] h-fit flex items-center gap-2 transition-all ease-in-out text-xs z-[100]">
                  <Icon name="copy" className="size-3.5 text-gray-text" />
                  Copied to clipboard
                </span>
              )
            : ''}
        </div>
        {canSkip && (
          <button
            type="button"
            className="text-xs text-blue-primary hover:text-gray-700 hover:cursor-pointer ml-1 underline"
            onClick={(e) => {
              e.stopPropagation();
              onSkip?.(goal);
            }}
          >
            Skip
          </button>
        )}
        {goal.isShareAction && (
          <button
            type="button"
            className="text-xs text-blue-primary hover:text-gray-700 hover:cursor-pointer ml-1 underline"
            onClick={(e) => {
              e.stopPropagation();
              onShare(goal);
            }}
          >
            Share
          </button>
        )}
      </div>
    </div>
  );
};

type Props = { profile: IUIProfile };

export const OnboardingBar = ({ profile }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isShared, setIsShared] = useState<boolean>(() =>
    readBool(STORAGE_KEYS.isShared, false),
  );
  const [isEarnSkipped, setIsEarnSkipped] = useState<boolean>(() =>
    readBool(STORAGE_KEYS.isEarnSkipped, false),
  );
  const [isMembershipSkipped, setIsMembershipSkipped] = useState<boolean>(() =>
    readBool(STORAGE_KEYS.isMembershipSkipped, false),
  );
  const [completedGoals, setCompletedGoals] = useState<number[]>(() =>
    readJSON<number[]>(STORAGE_KEYS.completedGoals, []),
  );
  const { copy, copiedText } = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  const { mutate: setOnboarded, isPending: isSettingOnboarded } =
    useSetOnboardedMutation();
  const firedRef = useRef(false);
  if (profile.isOnboarded) return null;

  const GOALS_LIST: IOnboardingGoal[] = [
    {
      id: 1,
      title: 'Add a profile photo & nickname',
      completed: !!profile.avatarUrl && !!profile.fullName,
      link: `/${profile.uniqueLink}`,
    },
    {
      id: 2,
      title: 'Connecting a way to earn',
      completed: profile.isStripeConnected || isEarnSkipped || profile.payment.isEnabled,
      link: `/finances`,
    },

    {
      id: 3,
      title: 'Add a Bio',
      completed: !!profile.bio,
      link: `/${profile.uniqueLink}`,
    },
    {
      id: 4,
      title: 'Set up a membership',
      completed: profile.hasTier || isMembershipSkipped,
      link: `/finances?tab=memberships`,
    },
    {
      id: 5,
      title: 'Share your Player 3 profile',
      completed: isShared,
      isShareAction: true,
    },
  ];

  const completedCount = GOALS_LIST.filter(
    (goal) => goal.completed || completedGoals.includes(goal.id),
  ).length;

  const totalCount = GOALS_LIST.length;
  const isAllDone = completedCount === totalCount;
  const progressPercentage = (completedCount / totalCount) * 100;

  useEffect(() => {
    if (!firedRef.current && isAllDone && !profile.isOnboarded) {
      firedRef.current = true;
      setOnboarded(undefined, {
        onError: () => {
          firedRef.current = false;
        },
      });
    }
  }, [isAllDone, profile.isOnboarded, setOnboarded]);

  const handleShare = async (goal: IOnboardingGoal) => {
    const profileData = {
      id: goal.id,
      action: 'share_profile',
      timestamp: new Date().toISOString(),
      completed: true,
    };

    storage.setItem(`shared_profile_${goal.id}`, JSON.stringify(profileData));

    const fullLink = `https://player3.io/${profile.uniqueLink}`;
    const success = await copy(fullLink);

    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);

      storage.setItem(STORAGE_KEYS.isShared, 'true');
      setIsShared(true);
    }

    const updatedCompleted = Array.from(new Set([...completedGoals, goal.id]));
    setCompletedGoals(updatedCompleted);
    storage.setItem(
      STORAGE_KEYS.completedGoals,
      JSON.stringify(updatedCompleted),
    );
  };

  const handleSkip = (goal: IOnboardingGoal) => {
    if (goal.id === 1) {
      storage.setItem(STORAGE_KEYS.isEarnSkipped, 'true');
      setIsEarnSkipped(true);
    } else if (goal.id === 4) {
      storage.setItem(STORAGE_KEYS.isMembershipSkipped, 'true');
      setIsMembershipSkipped(true);
    }

    const updatedCompleted = Array.from(new Set([...completedGoals, goal.id]));
    setCompletedGoals(updatedCompleted);
    storage.setItem(
      STORAGE_KEYS.completedGoals,
      JSON.stringify(updatedCompleted),
    );
  };

  return (
    <div className="relative lg:w-[260px] bg-transparent lg:py-1 lg:px-5 mx-3 md:mx-0 flex items-center">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            className="flex items-center justify-between w-full text-left hover:cursor-pointer transition-colors rounded-lg lg:py-2"
            onClick={() => setIsOpen((v) => !v)}
          >
            <div className="flex items-center md:gap-3 gap-1">
              <Icon
                name="chevron-down"
                className={cn(
                  `size-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`,
                )}
              />
              <div className="flex items-center lg:gap-3 flex-col lg:flex-row">
                <p
                  className={cn(
                    'md:text-sm text-xs font-normal text-black lg:flex hidden',
                  )}
                >
                  Get started
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-primary transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <span className={cn('text-sm text-black font-medium')}>
                    {completedCount}/{totalCount}
                  </span>
                </div>
              </div>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <div
            className={cn(
              'fixed inset-x-0 top-[52px] z-[60] w-screen rounded-none shadow-xl border-t md:absolute md:inset-auto md:left-1/2 md:-translate-x-1/2 md:top-full md:mt-2 md:w-auto md:max-w-[280px] md:rounded-lg md:border md:shadow-xl data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 data-[state=closed]:-translate-y-2 data-[state=open]:opacity-100 data-[state=open]:translate-y-0 transition-all duration-200 ease-out bg-white border-gray-100',
            )}
          >
            <GoalsList
              goals={GOALS_LIST.map((g) => ({
                ...g,
                completed: g.completed || completedGoals.includes(g.id),
              }))}
              onSkip={handleSkip}
              onShare={handleShare}
              isCopied={isCopied}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
